/**
 * The source switch for the section's writes: the same signatures over the mock and over ows-mail,
 * picked once by `MAILBOX_SOURCE` (see `source.ts`). Components never see which one answers; the
 * page's handlers call this, as they called the mocks before. Under `unavailable` nothing calls it:
 * the section and the mailbox page render their unavailable notice and never mount the live views.
 *
 * The reads that the mock derives from the brand's seed (the list, the domain, usage) branch in the
 * hooks that own that seed, not here.
 */
import { createMockMailbox, rotateMockMailboxPassword } from '../../../mock-data/mailbox-accounts';
import type { MailboxRecord, MailboxSso } from '../../../mock-data/mailbox-records';
import { requestMockMailboxSso } from '../../../mock-data/mailbox-sso';
import {
  createMailbox,
  deleteMailbox,
  fetchAccountSettings,
  isNotFound,
  requestSso,
  rotatePassword,
  type CreateMailboxBody,
  type CreateMailboxResult,
  type MailboxCredentials,
  type MailClientSettings,
} from './client';
import { MAILBOX_CREATE_TIMEOUT_MS } from '../state';
import { MAILBOX_SOURCE } from './source';

/** Run `call`, rejecting (with a plain `Error`: not the service's) once `ms` pass unanswered, and
 *  aborting the call's signal then so a live request is dropped rather than left open. */
const withTimeout = <T,>(call: (signal: AbortSignal) => Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const controller = new AbortController();
    const id = window.setTimeout(() => {
      controller.abort();
      reject(new Error('The create did not answer in time.'));
    }, ms);
    call(controller.signal).then(
      (value) => {
        window.clearTimeout(id);
        resolve(value);
      },
      (reason: unknown) => {
        window.clearTimeout(id);
        reject(reason);
      },
    );
  });

export type MailboxService = {
  /** Create a mailbox. Rejects with the service's error, or with a plain `Error` after
   *  {@link MAILBOX_CREATE_TIMEOUT_MS} unanswered; a `409` is never retried. */
  createMailbox: (domain: string, body: CreateMailboxBody) => Promise<CreateMailboxResult>;
  /** Rotate to `password` (the browser generates or the operator types it: the section never leaves
   *  it to the service), returned once. */
  rotatePassword: (domain: string, email: string, password: string) => Promise<MailboxCredentials>;
  deleteMailbox: (domain: string, email: string) => Promise<void>;
  /** The mail client settings for the client-setup table, when the source has them. */
  fetchAccountSettings: (domain: string, email: string) => Promise<MailClientSettings | undefined>;
  /** A single-use webmail sign-in, one per click. Never stored, logged or reused. */
  requestSso: (domain: string, mailbox: Pick<MailboxRecord, 'email' | 'status'>) => Promise<MailboxSso>;
};

/** What the one-call create reports on a domain it had to set up, in the order it does it. */
const FIRST_CREATE_STEPS = ['domain_registered', 'mail_order_created', 'dns_applied', 'mailbox_created'];

/** The mock's create, before the timeout every source shares. */
const createMock = async (body: CreateMailboxBody): Promise<CreateMailboxResult> => {
  const credentials = await createMockMailbox(body.email, 'ok', body.password);
  // A first create (it carries `manageDns`, which only the call that registers the domain sends) set
  // the domain up and published its records; any later one only made the mailbox. The live answer's lookup already had MX and SPF verified (the zone
  // is at the same registrar), so the mock says the same. No `mailDomain`: the reducer derives it,
  // as it did before the service existed.
  const first = body.manageDns === true;
  if (!first) return { credentials, steps: ['mailbox_created'], dnsPending: false };
  return { credentials, steps: FIRST_CREATE_STEPS, dnsPending: true, dns: { mxVerified: true, spfVerified: true } };
};

const mockService: MailboxService = {
  createMailbox: (_domain, body) => withTimeout(() => createMock(body), MAILBOX_CREATE_TIMEOUT_MS),
  rotatePassword: (_domain, email, password) => rotateMockMailboxPassword(email, password),
  deleteMailbox: () => Promise.resolve(),
  // The mock has no provider settings; the client guide shows its fixed rows.
  fetchAccountSettings: () => Promise.resolve(undefined),
  requestSso: (_domain, mailbox) => requestMockMailboxSso(mailbox),
};

const httpService: MailboxService = {
  createMailbox: (domain, body) => withTimeout((signal) => createMailbox(domain, body, signal), MAILBOX_CREATE_TIMEOUT_MS),
  rotatePassword: (domain, email, password) => rotatePassword(domain, email, password),
  // A `404` is the service never having had the mailbox: the row was stale, and it is gone either way.
  deleteMailbox: (domain, email) =>
    deleteMailbox(domain, email).catch((rejection: unknown) => {
      if (!isNotFound(rejection)) throw rejection;
    }),
  fetchAccountSettings,
  requestSso: (domain, mailbox) => requestSso(domain, mailbox.email),
};

export const mailboxService: MailboxService = MAILBOX_SOURCE === 'http' ? httpService : mockService;
